import {
  LiveRowMetadata,
  STRUCTURED_TABLE_KINDS_TO_SHOW_CSV_METADATA,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import buildRowUiData from 'notebook/structured-tables/util/buildRowUiData';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {isCellLoading} from 'notebook/tables/excess/Util';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {toFilename} from 'util/FileUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

export const TABLE_LOADING_CSV_DOWNLOAD_DISABLED_REASON = 'Wait for the table to finish loading';

/**
 * Returns why a StructuredTable can't be downloaded as a CSV yet, or null if it can.
 */
export function getStructuredTableCSVDownloadDisabledReason({
  structuredTable,
  computedTable,
}: {
  structuredTable: UnknownStructuredTable;
  computedTable: Array<Array<EvaluatedCell>>;
}): string | null {
  if (structuredTable.rows.length === 0) {
    return 'Add a row to download as CSV';
  }
  if (computedTable.some((row) => row.some((cell) => isCellLoading(cell)))) {
    return TABLE_LOADING_CSV_DOWNLOAD_DISABLED_REASON;
  }
  return null;
}

/**
 * Builds a CSV of a StructuredTable and starts the browser download. Does not record an audit event.
 */
export default async function downloadStructuredTableCSV({
  structuredTable,
  structuredTableAdapter,
  computedTable,
  liveRowMetadataByRow,
}: {
  structuredTable: UnknownStructuredTable;
  structuredTableAdapter: UnknownStructuredTableAdapter;
  computedTable: Array<Array<EvaluatedCell>>;
  liveRowMetadataByRow?: Array<LiveRowMetadata>;
}): Promise<void> {
  const tableName = structuredTable.name.length > 0 ? structuredTable.name : 'table';
  const table = structuredTableAdapter.toTable(structuredTable);
  initiateTextDownload(
    await makeTableCSV({
      table: table.table,
      computedTable,
      headerRow: table.columnLabels.map((columnLabel) => columnLabel || 'unknown'),
      rowUiData: STRUCTURED_TABLE_KINDS_TO_SHOW_CSV_METADATA.includes(structuredTable.kind)
        ? buildRowUiData(structuredTable, liveRowMetadataByRow)
        : undefined,
      includeCellValueModifiers: false,
    }),
    toFilename(tableName, 'csv')
  );
}
